import React from 'react';
import { Box, Container, Typography, Paper, Divider, Chip, Stack, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import GavelIcon from '@mui/icons-material/Gavel';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import ShieldIcon from '@mui/icons-material/Shield';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const AccentBubble = ({ top, left, size, opacity }: { top: string; left: string; size: number; opacity: number }) => (
  <Box sx={{
    position: 'absolute',
    top,
    left,
    width: size,
    height: size,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(231,47,13,0.25), rgba(31,31,31,0.2))',
    filter: 'blur(28px)',
    opacity,
    pointerEvents: 'none'
  }} />
);

const StoryPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#F2F2F2', position: 'relative', overflow: 'hidden' }}>
      {/* Soft background accents */}
      <AccentBubble top="6%" left="10%" size={240} opacity={0.4} />
      <AccentBubble top="20%" left="75%" size={320} opacity={0.35} />
      <AccentBubble top="70%" left="20%" size={260} opacity={0.25} />

      <Container maxWidth={false} sx={{ maxWidth: '1200px', px: { xs: 2, sm: 3 }, pt: { xs: 10, sm: 12 }, pb: { xs: 6, sm: 10 } }}>
        {/* Hero */}
        <Paper elevation={0} sx={{
          p: { xs: 3, sm: 5 },
          mb: 4,
          borderRadius: '28px',
          background: 'linear-gradient(135deg, rgba(31,31,31,0.96), rgba(58,58,58,0.9) 60%, rgba(231,47,13,0.85))',
          color: '#fff',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 20% 10%, rgba(255,255,255,0.08), transparent 45%)' }} />
          <Stack spacing={2} sx={{ position: 'relative' }}>
            <Chip label="Skutečný příběh" sx={{ width: 'fit-content', backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700 }} />
            <Typography component="h1" sx={{ fontFamily: 'Inter', fontWeight: 900, fontSize: { xs: '30px', sm: '40px' }, lineHeight: 1.15 }}>
              Příběh vzniku Právního Bota
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', maxWidth: 900, fontSize: { xs: '15px', sm: '17px' } }}>
              Jak se z frustrace z protichůdných právních rad a drahých telefonátů zrodil nástroj, který pomáhá lidem rozumět právu – rychle, srozumitelně a za zlomek ceny.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip icon={<GavelIcon sx={{ color: '#fff !important' }} />} label="Ověřené zdroje" sx={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600 }} />
              <Chip icon={<EmojiObjectsIcon sx={{ color: '#fff !important' }} />} label="Srozumitelnost" sx={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600 }} />
              <Chip icon={<ShieldIcon sx={{ color: '#fff !important' }} />} label="Dostupnost pro všechny" sx={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600 }} />
            </Stack>
          </Stack>
        </Paper>

        {/* Story + Reasons */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, gap: 3 }}>
          <Box>
            <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: '22px', backgroundColor: '#FFFFFF', boxShadow: '0 16px 44px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
              <Typography sx={{ fontSize: '18px', lineHeight: 1.8, color: '#1F1F1F', mb: 2 }}>
                Byla jednou jedna <strong>Eva Strejcová</strong>. Podnikavá, pracovitá a statečná. Jednoho dne se musela soudit s firmou,
                která jí nedodala, co slíbila. Obrátila se na právníky – a pokaždé slyšela něco jiného. Jeden radil tak, druhý přesně naopak.
                A každý telefonát stál majlant. Účtenky rostly, jistota ne. Spravedlnost se mezi protichůdnými radami ztrácela.
              </Typography>
              <Typography sx={{ fontSize: '18px', lineHeight: 1.8, color: '#1F1F1F', mb: 2 }}>
                Eva si řekla: „Musí existovat způsob, jak dát lidem jasnou, srozumitelnou a dostupnou právní informaci – bez astronomických
                částek za každou minutu hovoru a bez chaosu z deseti různých názorů.“ A tak vznikla myšlenka na <strong>Právního Bota</strong>.
                Spolu se svým manželem dali dohromady tým a technologii, která propojuje ověřené právní zdroje, konzistentní metodiku a moderní AI.
              </Typography>
              <Box sx={{ my: 2, p: { xs: 2, sm: 3 }, borderLeft: '4px solid #e72f0d', backgroundColor: 'rgba(231,47,13,0.06)', borderRadius: '12px' }}>
                <Typography sx={{ fontStyle: 'italic', color: '#1F1F1F' }}>
                  „K soudu už jedině s právnimbotem.“ — <strong>Eva Strejcová</strong>
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '18px', lineHeight: 1.8, color: '#1F1F1F' }}>
                Cíl byl jednoduchý: pomoci i „obyčejným“ lidem – živnostníkům, rodinám, malým firmám – porozumět právu, udělat správný první krok a nebát se zeptat.
                Za zlomek ceny oproti tradičním cestám. Právní Bot mluví česky, vysvětluje lidsky a ke každé odpovědi přidává jasná pravidla, ze kterých vychází.
              </Typography>
            </Paper>
          </Box>
          <Box>
            <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: '22px', backgroundColor: '#FFFFFF', boxShadow: '0 16px 44px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
              <Typography sx={{ fontWeight: 800, fontSize: '18px', mb: 1, color: '#1F1F1F' }}>Proč to vzniklo?</Typography>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <FavoriteBorderIcon sx={{ color: '#e72f0d' }} />
                  <Typography sx={{ color: '#424242' }}>Aby lidé měli <strong>jasnou</strong> a <strong>dostupnou</strong> právní informaci bez astronomických nákladů.</Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <GavelIcon sx={{ color: '#1F1F1F' }} />
                  <Typography sx={{ color: '#424242' }}>Aby rady byly <strong>konzistentní</strong> a opřené o <strong>ověřené zdroje</strong>.</Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <AutoFixHighIcon sx={{ color: '#e72f0d' }} />
                  <Typography sx={{ color: '#424242' }}>Aby právo bylo vysvětlené <strong>lidsky</strong> a <strong>srozumitelně</strong>.</Typography>
                </Stack>
              </Stack>
            </Paper>
          </Box>
        </Box>

        {/* Milestones */}
        <Box sx={{ mt: 4 }}>
          <Typography sx={{ fontFamily: 'Inter', fontWeight: 800, fontSize: { xs: '22px', sm: '26px' }, mb: 2, color: '#1F1F1F' }}>
            Milníky na cestě
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {[{
              n: '1',
              t: 'Frustrace z praxe',
              d: 'Protichůdné rady a vysoké účty. Impuls udělat to jinak.'
            }, {
              n: '2',
              t: 'Nápad a prototyp',
              d: 'Metodika + AI + právní zdroje. První verze pro blízké.'
            }, {
              n: '3',
              t: 'Pomoc tisícům',
              d: 'Služba pro veřejnost: srozumitelná, dostupná, ověřená.'
            }].map((m) => (
              <Box key={m.n}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: '18px', backgroundColor: '#FFFFFF', boxShadow: '0 12px 32px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <Chip label={m.n} sx={{ backgroundColor: '#e72f0d', color: '#fff', fontWeight: 700, mb: 1 }} />
                  <Typography sx={{ fontWeight: 700, color: '#1F1F1F', mb: 0.5 }}>{m.t}</Typography>
                  <Typography sx={{ color: '#424242' }}>{m.d}</Typography>
                </Paper>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Timeline */}
        <Box sx={{ mt: 5 }}>
          <Typography sx={{ fontFamily: 'Inter', fontWeight: 800, fontSize: { xs: '22px', sm: '26px' }, mb: 1, color: '#1F1F1F' }}>
            Časová osa
          </Typography>
          <Typography sx={{ color: '#424242', mb: 3 }}>
            Cesta od prvního nápadu až po službu pro veřejnost.
          </Typography>

          {/* Vertical timeline (no extra deps) */}
          <Box sx={{ position: 'relative', pl: { xs: 3, sm: 4 } }}>
            {/* line */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: { xs: 10, sm: 14 },
              width: '2px',
              background: 'linear-gradient(180deg, rgba(0,0,0,0.08), rgba(0,0,0,0.04))'
            }} />

            {([
              {
                t: 'Reálná zkušenost s rozporem v radách',
                d: 'Rozdílné právní názory a vysoké náklady za telefonáty ukázaly, že chybí srozumitelná a dostupná cesta.'
              },
              {
                t: 'Vize: právo srozumitelně',
                d: 'Cílem je poskytnout jasné právní informace a praktické kroky – bez chaosu a zbytečných výdajů.'
              },
              {
                t: 'Výzkum a metodika',
                d: 'Propojení ověřených zdrojů, konzistentních postupů a transparentního zobrazování odkazů.'
              },
              {
                t: 'První prototyp',
                d: 'Testování s blízkými a pilotní uživatelské scénáře – co je srozumitelné a co zlepšit.'
              },
              {
                t: 'Integrace AI a RAG',
                d: 'Vyhledávání v zákonech a judikatuře, ověřování citací, a kontinuální validace odpovědí.'
              },
              {
                t: 'Otevření veřejnosti',
                d: 'Zpřístupnění konzultací, analýz smluv a generování dokumentů široké veřejnosti.'
              },
              {
                t: 'Průběžné zlepšování',
                d: 'Pravidelné aktualizace, lepší vysvětlování a nové funkce dle zpětné vazby uživatelů.'
              }
            ] as Array<{t: string; d: string}>).map((s, i) => (
              <Box key={s.t} sx={{ position: 'relative', pl: { xs: 3, sm: 4 }, py: 1.5 }}>
                {/* dot */}
                <Box sx={{
                  position: 'absolute',
                  left: { xs: 6, sm: 10 },
                  top: 20,
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: '#e72f0d',
                  boxShadow: '0 0 0 4px rgba(231,47,13,0.12)'
                }} />

                <Paper elevation={0} sx={{ p: 2, borderRadius: '14px', backgroundColor: '#FFFFFF', boxShadow: '0 10px 26px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip size="small" label={`${i + 1}`} sx={{ backgroundColor: '#1F1F1F', color: '#fff', fontWeight: 700 }} />
                    <Typography sx={{ fontWeight: 700, color: '#1F1F1F' }}>{s.t}</Typography>
                  </Stack>
                  <Typography sx={{ color: '#424242' }}>{s.d}</Typography>
                </Paper>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Closing */}
        <Paper elevation={0} sx={{ mt: 4, p: { xs: 3, sm: 4 }, borderRadius: '22px', backgroundColor: '#FFFFFF', boxShadow: '0 16px 44px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography sx={{ fontSize: '18px', lineHeight: 1.8, color: '#1F1F1F' }}>
            Když přijde čas na skutečného advokáta, přicházíte <strong>připraveni</strong>: s přehledem, podklady a otázkami. A i to je část
            spravedlnosti – mít šanci rozumět.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ color: '#424242', mb: 2 }}>
            Poznámka: Právní Bot poskytuje obecné právní informace. Nenahrazuje individuální právní poradenství. V komplikovaných
            případech doporučujeme obrátit se na advokáta.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={() => navigate('/dashboard')} sx={{ textTransform: 'none', borderRadius: '12px', backgroundColor: '#1F1F1F', '&:hover': { backgroundColor: '#e72f0d' } }}>Otevřít aplikaci</Button>
            <Button variant="outlined" onClick={() => navigate('/pricing')} sx={{ textTransform: 'none', borderRadius: '12px', borderColor: '#1F1F1F', color: '#1F1F1F', '&:hover': { borderColor: '#e72f0d', color: '#e72f0d' } }}>Přejít na ceník</Button>
          </Stack>
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
};

export default StoryPage;
